import { createGlobalStyle } from 'styled-components';

import { ds } from '@/config/colors';
import { media } from '@/utils/mediaQueries';

/*
 * Tabele zamieniane w listy. Pomijamy siatki kalendarzy HeroUI (`data-slot="grid"`)
 * i tabele, które widok świadomie zostawia (`data-mobile-table="keep"`).
 */
export const TABLE_SELECTOR = 'table:not([data-slot="grid"]):not([data-mobile-table="keep"])';

/*
 * Telefon: każdy wiersz tabeli to karta listy. Pierwsza kolumna jest tytułem karty,
 * kolejne idą pod nią jako pary „etykieta kolumny: wartość”. Nagłówek tabeli znika
 * (etykiety niesie każda komórka), a szerokości minimalne i przewijanie w bok są
 * wyłączone, żeby wszystko mieściło się na ekranie.
 */
export const MobileTablesStyles = createGlobalStyle<{ $containerId: string }>`
  ${media.mobile} {
    #${({ $containerId }) => $containerId} {
      /* Kontenery tabel z overflow-x i min-width nie mogą rozpychać strony. */
      *:has(> ${TABLE_SELECTOR}) {
        overflow-x: visible !important;
        min-width: 0 !important;
        max-width: 100% !important;
      }

      /* Paginacja HeroUI na środku (kontenery widoków ustawiają ją do prawej). */
      *:has(> nav[data-slot='base'] > [data-slot='wrapper']) {
        justify-content: center !important;
      }

      nav[data-slot='base']:has(> [data-slot='wrapper']) {
        margin-inline: auto;
        max-width: 100%;
      }

      ${TABLE_SELECTOR} {
        display: block !important;
        width: 100% !important;
        min-width: 0 !important;
        table-layout: auto !important;
        border-collapse: separate;

        thead {
          display: none !important;
        }

        tbody,
        tfoot {
          display: flex !important;
          flex-direction: column;
          gap: 8px;
          width: 100%;
        }

        /* Karta: pierwszy wiersz to ikony/checkbox i tytuł, pod nim pola na całą szerokość.
           Ramka i odstępy są wspólne dla wszystkich widoków (nadpisują style tabel). */
        tr {
          display: flex !important;
          flex-flow: row wrap;
          align-items: flex-start;
          column-gap: 10px;
          row-gap: 6px;
          width: 100%;
          height: auto !important;
          margin: 0 !important;
          padding: 12px 14px !important;
          border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : ds.borderDefault)} !important;
          border-radius: 12px !important;
          box-shadow: none !important;
        }

        /* Zaznaczenie wiersza HeroUI rysuje ::before komórek, które na karcie niosą etykiety;
           zaznaczoną kartę wyróżniamy więc tłem całego wiersza. */
        tr[data-selected='true'] {
          background: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.background.tertiary : ds.bgMuted)} !important;
        }

        td {
          display: flex !important;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          flex: 0 0 100%;
          width: 100% !important;
          min-width: 0 !important;
          max-width: none !important;
          height: auto !important;
          padding: 0 !important;
          border: none !important;
          text-align: right;
          white-space: normal !important;
          overflow-wrap: anywhere;
          font-size: 14px;
          line-height: 1.45;

          /* Komórka HeroUI ma własne ::before (absolutne, przezroczyste tło zaznaczenia),
             które przejmowało etykietę; tu wracamy do zwykłego elementu w przepływie. */
          &::before {
            content: attr(data-label);
            position: static;
            inset: auto;
            z-index: auto;
            opacity: 1;
            background: none;
            border-radius: 0;
            flex-shrink: 0;
            max-width: 45%;
            text-align: left;
            font-size: 13px;
            font-weight: 400;
            color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : ds.textMuted)};
          }

          &:not([data-label])::before,
          &[data-label='']::before {
            display: none;
          }

          /* Bez etykiety (np. kolumna akcji) wartość leży po lewej, jak reszta karty. */
          &:not([data-label]),
          &[data-label=''] {
            justify-content: flex-start;
            text-align: left;
          }

          &:empty,
          &[data-mobile-empty] {
            display: none !important;
          }

          /* Zawartość komórek (flexy, przyciski, plakietki) może się zawijać. */
          > * {
            min-width: 0;
            max-width: 100%;
            flex-wrap: wrap;
          }
        }

        /* Checkbox, ikona i miniatura w linii z tytułem karty (środek wiersza, nie góra). */
        td[data-mobile-lead] {
          flex: 0 0 auto;
          width: auto !important;
          min-height: 30px;
          align-items: center;
          align-self: center;

          /* Miniatura, ikona czy checkbox nie potrzebują etykiety kolumny. */
          &::before {
            display: none;
          }
        }

        /* Tytuł karty: pierwsza kolumna z tekstem, bez etykiety, zawija się zamiast ucinać. */
        td[data-mobile-title] {
          flex: 1 1 0;
          min-width: 0;
          min-height: 30px;
          justify-content: flex-start;
          align-items: center;
          align-self: center;
          text-align: left;
          font-size: 15px;
          font-weight: 500;
          color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : ds.textPrimary)};

          &::before {
            display: none;
          }

          * {
            white-space: normal !important;
            text-overflow: clip !important;
            max-width: 100% !important;
          }

          /* Kolejne bloki tekstu w tytule (np. opis pod nazwą) idą pod spód jako opis. */
          flex-wrap: wrap;
          row-gap: 2px;

          > div + div {
            flex-basis: 100%;
            font-size: 13px;
            font-weight: 400;
            color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : ds.textMuted)};
          }
        }

        /* Edycja nazwy w miejscu: pole i przyciski na całą szerokość tytułu, bez stałej
           minimalnej szerokości z widoku desktopowego. */
        td[data-mobile-title]:has(input[type='text']) > * {
          display: flex;
          width: 100%;
          flex-wrap: nowrap;
        }

        td[data-mobile-title] input[type='text'] {
          flex: 1 1 auto;
          width: 0;
          min-width: 0 !important;
        }

        /* Akcje pokazywane na desktopie dopiero po najechaniu (np. ołówek przy nazwie):
           na telefonie nie ma hovera, więc są widoczne zawsze. */
        .group-hover\:opacity-100,
        .group-hover\:visible {
          opacity: 1 !important;
          visibility: visible !important;
        }

        /* Komórki scalone (pusty stan, rozwinięty wiersz) zajmują całą kartę. */
        td[colspan]:not([colspan='1']) {
          display: block !important;
          text-align: left;

          &::before {
            display: none;
          }
        }
      }
    }
  }
`;
